import React, { FunctionComponent, useRef, ReactNode, useState, ReactElement, RefObject } from 'react';
import { Popover, Overlay } from 'react-bootstrap';
import styles from './styles.module.scss';
import { useOnClickOutside } from '../../common/helpers/customHooks';
import { concat } from '../../common/helpers/arrayHelper';
import { IBindingAction } from '../../common/models/callback';

interface ICustomPopoverProps {
  trigger?: ReactNode;
  placement?: 'bottom' | 'bottom-start' | 'bottom-end' | 'top' | 'top-start' | 'top-end';
  label?: string | JSX.Element;
  rootClose?: boolean | 'custom';
  popoverClassName?: string;
  container?: RefObject<HTMLElement>;
  showArrow?: boolean;
  onHideAction?: IBindingAction;
}

const CustomPopover: FunctionComponent<ICustomPopoverProps> = ({
  trigger,
  children,
  placement = 'bottom',
  label,
  rootClose = false,
  popoverClassName = '',
  container,
  showArrow = false,
  onHideAction = () => null
}) => {
  const targetRef = useRef<any>(null);
  const [show, setShow] = useState(false);

  const defaultTrigger = () => (
    <div className={`d-flex justivy-content-center align-items-center ${styles.defaultTrigger}`}>
      {label}
    </div>
  );

  const hidePopover = () => {
    setShow(false);
    onHideAction();
  };

  const onTriggerClick = () => setShow(rootClose === 'custom' ? true : !show);

  if (rootClose === 'custom' && container) {
    useOnClickOutside(container, () => hidePopover());
  }

  const isBottomPopover = placement === 'bottom' || placement === 'bottom-start' || placement === 'bottom-end';

  return (
    <div
      ref={targetRef}
      className={`cursor-pointer ${styles.trigger} ${show ? `${styles.opened} opened` : `${styles.closed} closed`}`}
    >
      <div role="presentation" onClick={onTriggerClick}>{trigger || defaultTrigger}</div>
      <Overlay
        rootClose={rootClose !== 'custom' && rootClose}
        onHide={hidePopover}
        placement={placement}
        show={show}
        target={targetRef}
        container={container}
      >
        <Popover
          id="popup"
          bsPrefix={concat(
            showArrow && 'popover',
            styles.customPopover,
            popoverClassName,
            isBottomPopover ? styles.bottomPopover : styles.topPopover
          )}
        >
          {React.cloneElement(children as ReactElement<any>, { hidePopover })}
        </Popover>
      </Overlay>
    </div>
  );
};

export default CustomPopover;
